/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

// Type-only import (erased at runtime, so it doesn't eagerly pull in the cml barrel).
import type { BasicMarkdownEditorHandle } from '@coursera-web/cml';

import { Typography2 } from '@coursera/cds-core';

// Lazy-load the Slate-based editor (via a local chunk module) so importing this
// module — and the whole admin app graph that pulls it in via the messaging
// barrel — doesn't eagerly evaluate `@coursera-web/cml`. The cml barrel
// transitively loads CMLEditorGraphQL → AssetAdminManager, which runs a
// module-load Flux `connectToStores` that throws when the store isn't
// registered (e.g. in app-level unit tests). Lazy-loading also keeps the heavy
// editor out of the initial bundle; it only loads when the editor actually
// renders (inside an opened modal), so SSR is unaffected.
const BasicMarkdownEditor = React.lazy(
  () => import('bundles/enterprise-admin-messaging-v2/components/common/BasicMarkdownEditorChunk')
);

const DEFAULT_MIN_HEIGHT = 220;

// Toolbar set mirrors the design: paragraph style, B / I / U, link / unlink,
// subscript / superscript. No lists / tables / code.
const TOOLS = [
  'HEADING_1',
  'HEADING_2',
  'HEADING_3',
  'HEADING_4',
  'BODY1',
  'BOLD',
  'ITALIC',
  'UNDERLINE',
  'LINK',
  'UNLINK',
  'SUBSCRIPT',
  'SUPERSCRIPT',
] as const;

/** Inserts text at the editor's current caret (or end of doc if unfocused). */
export type InsertText = (text: string) => void;

type Props = {
  /** Message body as markdown. */
  value: string;
  /** Fired with the updated markdown. */
  onChange: (markdown: string) => void;
  /**
   * Optional render prop for controls on the editor's bottom toolbar row,
   * right-aligned (e.g. personalization-tag buttons). Receives `insertText` so
   * the controls can insert at the caret. Omit it for a plain editor.
   */
  renderToolbarActions?: (insertText: InsertText) => React.ReactNode;
  /**
   * When true, the editor is replaced by a non-interactive placeholder — used
   * while an AI rewrite is in flight on the Review-and-send screen.
   */
  disabled?: boolean;
  /** Text shown in place of the editor while `disabled`. */
  disabledLabel?: string;
  /** Floor the editor can shrink to when resized, in px. */
  minHeight?: number;
  /**
   * Initial rendered height of the editor box, in px. Falls back to the CML
   * resizable default (320px) when omitted. The box is fixed at this height:
   * taller content scrolls inside it, the box does not grow to fit.
   */
  maxHeight?: number;
  'aria-label'?: string;
  'data-testid'?: string;
  /**
   * Fired when focus enters the editor (the editable area or its toolbar).
   * Focus events bubble, so this is attached to the editor's container.
   */
  onFocus?: React.FocusEventHandler<HTMLDivElement>;
};

const styles = {
  disabled: (minHeight: number) => css`
    display: flex;
    align-items: center;
    min-height: ${minHeight}px;
    padding: var(--cds-spacing-200);
    border: 1px solid var(--cds-color-neutral-stroke-primary-weak);
    border-radius: var(--cds-border-radius-100);
    color: var(--cds-color-neutral-primary-weak);
    background: var(--cds-color-neutral-background-primary-weak);
  `,
  container: css`
    position: relative;
  `,
  // Placeholder while the lazy editor chunk loads — reserves the editor's height
  // so the layout doesn't jump.
  loading: (minHeight: number) => css`
    min-height: ${minHeight}px;
    border: 1px solid var(--cds-color-neutral-stroke-primary-weak);
    border-radius: var(--cds-border-radius-100);
  `,
  // Toolbar actions sit on the editor's bottom toolbar row, right-aligned. The
  // toolbar's tools are left-aligned, leaving the right side free. 53px mirrors
  // cml's ResizableCMLEditor TOOLBAR_HEIGHT so the actions vertically center on
  // that row; z-index clears the sticky toolbar (which has z-index: 1 and an
  // opaque background). pointer-events are scoped to the controls so the overlay
  // never blocks the toolbar tools or the resize handle.
  toolbarActions: css`
    position: absolute;
    right: var(--cds-spacing-200);
    bottom: 0;
    z-index: 2;
    height: 53px;
    display: flex;
    align-items: center;
    gap: var(--cds-spacing-100);
    pointer-events: none;

    & > * {
      pointer-events: auto;
    }
  `,
};

/**
 * Shared rich-text message editor used by the AI Review-and-send, manual draft,
 * and edit-and-resend flows. Authors in **markdown** (the email body is compiled
 * to HTML at send via `mockCompileEmailHtml`).
 *
 * The editor is agnostic about toolbar add-ons: pass `renderToolbarActions` to
 * surface controls (e.g. personalization tags) on the toolbar row with access
 * to `insertText`; omit it for a plain editor.
 *
 * SSR-safe: only ever mounted inside a modal that opens on user interaction, so
 * the Slate editor never renders on the server.
 */
const MessageBodyEditor = ({
  value,
  onChange,
  renderToolbarActions,
  disabled = false,
  disabledLabel,
  minHeight = DEFAULT_MIN_HEIGHT,
  maxHeight,
  'aria-label': ariaLabel,
  'data-testid': testId,
  onFocus,
}: Props) => {
  const editorRef = React.useRef<BasicMarkdownEditorHandle>(null);

  const insertText = React.useCallback<InsertText>((text) => {
    editorRef.current?.insertText(text);
  }, []);

  if (disabled) {
    return (
      <div css={styles.disabled(minHeight)} data-testid={testId}>
        <Typography2 component="span" variant="bodyPrimary">
          {disabledLabel}
        </Typography2>
      </div>
    );
  }

  return (
    <div css={styles.container} data-testid={testId} onFocus={onFocus}>
      <React.Suspense fallback={<div css={styles.loading(minHeight)} />}>
        <BasicMarkdownEditor
          ref={editorRef}
          value={value}
          onChange={onChange}
          customTools={[...TOOLS]}
          toolbarPosition="bottom"
          renderMode={{ typeName: 'resizable', minHeight, maxHeight }}
          aria-label={ariaLabel}
        />
      </React.Suspense>
      {renderToolbarActions && <div css={styles.toolbarActions}>{renderToolbarActions(insertText)}</div>}
    </div>
  );
};

export default MessageBodyEditor;
